/**
 * Geometry for placing daily RENO predictions on the drying graph's x axis.
 *
 * Extracted from `RenoTimeSeries.vue` so it can be unit-tested: getting it wrong makes the strip go
 * silently blank, which is exactly how the bug it fixes went unnoticed.
 *
 * Two facts drive all of it:
 *
 * - A prediction dated D describes the **day ending at D**, not the day starting at it. The daily run
 *   happens the following morning, so a drying on the 15th is scored by the entry dated the 16th.
 * - The graph's x unit is **not** days. It is minutes below a 3 h drying, hours below 2 days, and only
 *   then n-day steps (`TimeScale` in `DryingProgressGraphDataExtractor.ts`). A daily entry anchored at
 *   midnight therefore sits far outside the axis window of any drying shorter than a day: a drying from
 *   08:50 to 10:44 spans x 530 to 644 in minutes, while its entry sits at x 1440.
 */

/** The part of `TimeScale` this needs. `visibleRange.timeScale` is untyped in the component. */
export interface AxisTimeScale {
  scaleMinutes(minutes: number, ignoreOffset?: boolean): number;
}

/** The x range the graph is currently showing, in axis units. */
export interface AxisWindow {
  xMin: number;
  xMax: number;
}

/** An x range in axis units. */
export interface AxisInterval {
  start: number;
  end: number;
}

const MINUTES_IN_DAY = 60 * 24;

/**
 * One day expressed in the graph's own x-axis unit: 1440 for minutes, 24 for hours, `1 / multiplier`
 * for day steps.
 *
 * `ignoreOffset` because this is a length, not a point in time: the daylight-saving correction
 * `scaleMinutes` applies past its threshold would make a day 60 minutes longer or shorter here.
 */
export function dayInAxisUnits(timeScale: AxisTimeScale): number {
  return timeScale.scaleMinutes(MINUTES_IN_DAY, true);
}

/**
 * The interval a daily entry describes, given the x of its date (midnight) and the length of a day.
 *
 * @param entryX x of the entry's own date, i.e. the **end** of the day it scores.
 */
export function dailyEntryInterval(
  entryX: number,
  dayLength: number
): AxisInterval {
  return { start: entryX - dayLength, end: entryX };
}

/**
 * Whether a daily entry has anything to say about what is on screen.
 *
 * An **overlap** test, not a "is its date inside the window" test. The latter is what hid every score
 * on dryings shorter than two days, whose window never contains a midnight.
 */
export function isDailyEntryVisible(
  interval: AxisInterval,
  window: AxisWindow
): boolean {
  return interval.end >= window.xMin && interval.start <= window.xMax;
}

/**
 * Keeps a dot on the strip.
 *
 * The scored day usually ends after the drying does, so its unclamped x lies past `xMax` and the dot
 * would be drawn outside the SVG: present in the data, invisible on screen.
 */
export function clampToWindow(x: number, window: AxisWindow): number {
  return Math.min(Math.max(x, window.xMin), window.xMax);
}
